// ==================== KEYWORD BOTS PAGE ====================
// Manage auto-reply bots based on keywords

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot,
  Plus,
  Search,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  MessageSquare,
  Zap,
  Type,
  ChevronDown,
  AlertCircle,
  Loader2,
  X,
  Check,
  Settings,
  BarChart3,
} from 'lucide-react';
import { Button, Input, Modal, Select, ConfirmDialog } from '../../../components/ui';
import {
  keywordBotsService,
  type KeywordBot,
  type CreateKeywordBotData,
  type KeywordMatchType,
  type BotReplyType,
  type QuickReplyButton,
} from '../../../services/keyword-bots.service';
import { templatesService, type TemplateItem } from '../../../services/templates.service';
import { fadeIn, staggerContainer, staggerItem } from '../../../utils/animations';

// ==================== CONSTANTS ====================

const MATCH_TYPE_OPTIONS: { value: KeywordMatchType; label: string; description: string; icon: React.ReactNode }[] = [
  { value: 'EXACT', label: 'Exact Match', description: 'Message equals keyword exactly', icon: <Check className="w-4 h-4" /> },
  { value: 'CONTAINS', label: 'Contains', description: 'Message contains keyword', icon: <Search className="w-4 h-4" /> },
  { value: 'STARTS_WITH', label: 'Starts With', description: 'Message begins with keyword', icon: <Type className="w-4 h-4" /> },
  { value: 'ENDS_WITH', label: 'Ends With', description: 'Message ends with keyword', icon: <Type className="w-4 h-4" /> },
];

const REPLY_TYPE_OPTIONS: { value: BotReplyType; label: string; icon: React.ReactNode }[] = [
  { value: 'TEXT', label: 'Text Message', icon: <MessageSquare className="w-4 h-4" /> },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

// Available variables for reply messages
const REPLY_VARIABLES = [
  { variable: '{{name}}', label: 'Name', description: 'Contact name' },
  { variable: '{{phone}}', label: 'Phone', description: 'Phone number' },
  { variable: '{{email}}', label: 'Email', description: 'Email address' },
  { variable: '{{company}}', label: 'Company', description: 'Company name' },
  { variable: '{{country}}', label: 'Country', description: 'Country code' },
];

// ==================== BOT CARD COMPONENT ====================

interface BotCardProps {
  bot: KeywordBot;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
}

const BotCard = ({ bot, onEdit, onDelete, onToggle }: BotCardProps) => {
  const matchTypeInfo = MATCH_TYPE_OPTIONS.find((m) => m.value === bot.matchType);

  return (
    <motion.div
      variants={staggerItem}
      className={`bg-[#121723] border rounded-xl p-4 transition-all ${
        bot.isActive ? 'border-gray-800 hover:border-gray-700' : 'border-gray-800/50 opacity-60'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            bot.isActive ? 'bg-(--color-signal-orange)/20' : 'bg-gray-700/50'
          }`}>
            <Bot className={`w-5 h-5 ${bot.isActive ? 'text-(--color-signal-orange)' : 'text-gray-500'}`} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">{bot.name}</h3>
            <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
              {matchTypeInfo?.icon}
              {matchTypeInfo?.label}
            </p>
          </div>
        </div>
        <button
          onClick={onToggle}
          className={`transition-colors ${bot.isActive ? 'text-green-500' : 'text-gray-500'}`}
          title={bot.isActive ? 'Active - Click to disable' : 'Inactive - Click to enable'}
        >
          {bot.isActive ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
        </button>
      </div>

      {/* Description */}
      {bot.description && (
        <p className="text-xs text-gray-400 mb-3 line-clamp-2">{bot.description}</p>
      )}

      {/* Keywords */}
      <div className="mb-3">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Keywords</p>
        <div className="flex flex-wrap gap-1">
          {bot.keywords.slice(0, 5).map((keyword, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-[#1A2332] rounded text-xs text-gray-300"
            >
              {keyword}
            </span>
          ))}
          {bot.keywords.length > 5 && (
            <span className="px-2 py-0.5 bg-[#1A2332] rounded text-xs text-gray-400">
              +{bot.keywords.length - 5} more
            </span>
          )}
        </div>
      </div>

      {/* Reply Info */}
      <div className="mb-3 p-2 bg-[#0D1117] rounded-lg">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Reply</p>
        {bot.replyType === 'TEXT' ? (
          <p className="text-xs text-gray-300 line-clamp-2">{bot.replyContent}</p>
        ) : (
          <p className="text-xs text-gray-300">
            Template: <span className="text-(--color-signal-orange)">{bot.template?.name || 'N/A'}</span>
          </p>
        )}
      </div>

      {/* Stats & Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-800">
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <BarChart3 className="w-3 h-3" />
            {bot._count?.triggers || 0} triggers
          </span>
          {bot.priority > 0 && (
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Priority {bot.priority}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="p-1.5 rounded-lg hover:bg-[#1A2332] text-gray-400 hover:text-white transition-colors"
            title="Edit"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================

const KeywordBotsPage = () => {
  // State
  const [bots, setBots] = useState<KeywordBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Local input state for debouncing
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [editingBot, setEditingBot] = useState<KeywordBot | null>(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'delete' | 'activate' | 'deactivate';
    bot: KeywordBot | null;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'delete',
    bot: null,
  });

  // Form state
  const [formData, setFormData] = useState<CreateKeywordBotData>({
    name: '',
    description: '',
    keywords: [],
    matchType: 'CONTAINS',
    caseSensitive: false,
    replyType: 'TEXT',
    replyContent: '',
    quickReplyButtons: [],
    templateId: '',
    isActive: true,
    priority: 0,
    cooldownMinutes: undefined,
    enableSchedule: false,
    scheduleStart: '09:00',
    scheduleEnd: '18:00',
    scheduleDays: [1, 2, 3, 4, 5],
    timezone: 'UTC',
    welcomeInactivityHours: 24,
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ==================== DATA FETCHING ====================

  // Identifies the most recent fetchBots run, so stale ones can be discarded.
  const fetchIdRef = useRef(0);

  const fetchBots = useCallback(async () => {
    // Spans several requests, so tag each run and drop any that finishes after
    // a newer one has started - otherwise a slow early fetch can overwrite it.
    const fetchId = ++fetchIdRef.current;
    const isStale = () => fetchId !== fetchIdRef.current;

    try {
      setLoading(true);
      // Only search if query is empty or has at least 3 characters
      const shouldSearch = !searchQuery || searchQuery.length >= 3;
      // The page renders every bot in one grid with no pager, so load them all.
      const items = await keywordBotsService.getAllPages({
        search: shouldSearch ? searchQuery || undefined : undefined,
        isActive: filterActive,
      });

      if (isStale()) return;
      setBots(items);
    } catch (err) {
      if (isStale()) return;
      console.error('Failed to fetch bots:', err);
      setError('Failed to load keyword bots');
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [searchQuery, filterActive]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await templatesService.getAll({ status: 'APPROVED', limit: 100 });
      setTemplates(response.items);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update search query if input is empty or has at least 3 characters
      if (searchInput === '' || searchInput.length >= 3) {
        setSearchQuery(searchInput);
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // ==================== HANDLERS ====================

  const handleOpenModal = (bot?: KeywordBot) => {
    if (bot) {
      setEditingBot(bot);
      setFormData({
        name: bot.name,
        description: bot.description || '',
        keywords: bot.keywords,
        matchType: bot.matchType,
        caseSensitive: bot.caseSensitive,
        replyType: bot.replyType,
        replyContent: bot.replyContent || '',
        quickReplyButtons: bot.quickReplyButtons || [],
        templateId: bot.templateId || '',
        isActive: bot.isActive,
        priority: bot.priority,
        cooldownMinutes: bot.cooldownMinutes || undefined,
        enableSchedule: bot.enableSchedule,
        scheduleStart: bot.scheduleStart || '09:00',
        scheduleEnd: bot.scheduleEnd || '18:00',
        scheduleDays: bot.scheduleDays || [1, 2, 3, 4, 5],
        timezone: bot.timezone || 'UTC',
        welcomeInactivityHours: bot.welcomeInactivityHours || 24,
      });
    } else {
      setEditingBot(null);
      setFormData({
        name: '',
        description: '',
        keywords: [],
        matchType: 'CONTAINS',
        caseSensitive: false,
        replyType: 'TEXT',
        replyContent: '',
        quickReplyButtons: [],
        templateId: '',
        isActive: true,
        priority: 0,
        cooldownMinutes: undefined,
        enableSchedule: false,
        scheduleStart: '09:00',
        scheduleEnd: '18:00',
        scheduleDays: [1, 2, 3, 4, 5],
        timezone: 'UTC',
        welcomeInactivityHours: 24,
      });
    }
    setKeywordInput('');
    setShowAdvanced(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBot(null);
    setError(null);
  };

  const handleAddKeyword = () => {
    const keyword = keywordInput.trim();
    if (keyword && !formData.keywords.includes(keyword)) {
      setFormData((prev) => ({ ...prev, keywords: [...prev.keywords, keyword] }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== keyword),
    }));
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  // Form validation
  const isFormValid = (): boolean => {
    // Bot name is required
    if (!formData.name.trim()) return false;
    
    if (formData.keywords.length === 0) return false;
    
    // Reply content validation based on reply type
    if (formData.replyType === 'TEXT' && !formData.replyContent?.trim()) return false;
    if (formData.replyType === 'TEMPLATE' && !formData.templateId) return false;
    
    return true;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validation
      if (!formData.name.trim()) {
        throw new Error('Bot name is required');
      }
      if (formData.keywords.length === 0) {
        throw new Error('At least one keyword is required');
      }
      if (formData.replyType === 'TEXT' && !formData.replyContent?.trim()) {
        throw new Error('Reply content is required for text replies');
      }
      if (formData.replyType === 'TEMPLATE' && !formData.templateId) {
        throw new Error('Please select a template');
      }

      // Clean up the data - convert empty strings to undefined
      const cleanedData = {
        ...formData,
        templateId: formData.templateId && formData.templateId.trim() !== '' ? formData.templateId : undefined,
        replyContent: formData.replyContent && formData.replyContent.trim() !== '' ? formData.replyContent : undefined,
        description: formData.description && formData.description.trim() !== '' ? formData.description : undefined,
      };

      if (editingBot) {
        await keywordBotsService.update(editingBot.id, cleanedData);
      } else {
        await keywordBotsService.create(cleanedData);
      }

      handleCloseModal();
      fetchBots();
    } catch (err) {
      const errorMessage = (err as Error).message || 'Failed to save bot';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (bot: KeywordBot) => {
    setConfirmDialog({
      isOpen: true,
      title: bot.isActive ? 'Deactivate Bot' : 'Activate Bot',
      message: bot.isActive
        ? `Are you sure you want to deactivate "${bot.name}"? The bot will stop responding to incoming messages.`
        : `Are you sure you want to activate "${bot.name}"? The bot will start responding to incoming messages.`,
      type: bot.isActive ? 'deactivate' : 'activate',
      bot,
    });
  };

  const handleDelete = (bot: KeywordBot) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Bot',
      message: `Are you sure you want to delete "${bot.name}"? This action cannot be undone and all trigger history will be lost.`,
      type: 'delete',
      bot,
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.bot) return;

    try {
      if (confirmDialog.type === 'delete') {
        await keywordBotsService.delete(confirmDialog.bot.id);
      } else if (confirmDialog.type === 'activate' || confirmDialog.type === 'deactivate') {
        await keywordBotsService.toggleActive(confirmDialog.bot.id);
      }
      fetchBots();
      setConfirmDialog({ isOpen: false, title: '', message: '', type: 'delete', bot: null });
    } catch (err) {
      const errorMessage = (err as Error).message || 'Failed to perform action';
      console.error('Failed to perform action:', err);
      setError(errorMessage);
    }
  };

  // ==================== RENDER ====================

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-heading font-bold text-white flex items-center gap-2">
            <Bot className="w-6 h-6 text-(--color-signal-orange)" />
            Keyword Bots
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Create auto-reply bots that respond to specific keywords
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create Bot
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-[#121723] border border-gray-800 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Search bots by name or keywords (min 3 chars)..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              leftIcon={<Search className="w-4 h-4 text-gray-400" />}
            />
            {searchInput.length > 0 && searchInput.length < 3 && (
              <p className="text-xs text-yellow-500 mt-1">
                Please enter at least 3 characters to search
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 whitespace-nowrap">Status:</span>
              <div className="flex rounded-lg border border-gray-700 overflow-hidden">
                <button
                  onClick={() => setFilterActive(undefined)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    filterActive === undefined
                      ? 'bg-(--color-signal-orange) text-white'
                      : 'bg-transparent text-gray-400 hover:text-white hover:bg-[#1A2332]'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterActive(true)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-700 ${
                    filterActive === true
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-transparent text-gray-400 hover:text-white hover:bg-[#1A2332]'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setFilterActive(false)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-700 ${
                    filterActive === false
                      ? 'bg-gray-500/20 text-gray-300'
                      : 'bg-transparent text-gray-400 hover:text-white hover:bg-[#1A2332]'
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Results count */}
        {!loading && (
          <div className="mt-3 pt-3 border-t border-gray-800/50">
            <p className="text-xs text-gray-500">
              {bots.length} {bots.length === 1 ? 'bot' : 'bots'} found
              {searchQuery && <span className="text-gray-400"> for "{searchQuery}"</span>}
              {filterActive !== undefined && (
                <span className="text-gray-400"> • {filterActive ? 'Active only' : 'Inactive only'}</span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-(--color-signal-orange) animate-spin" />
        </div>
      ) : bots.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-[#1A2332] flex items-center justify-center mx-auto mb-4">
            <Bot className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No keyword bots yet</h3>
          <p className="text-sm text-gray-400">
            Click the <span className="text-(--color-signal-orange) font-medium">"Create Bot"</span> button above to get started
          </p>
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {bots.map((bot) => (
            <BotCard
              key={bot.id}
              bot={bot}
              onEdit={() => handleOpenModal(bot)}
              onDelete={() => handleDelete(bot)}
              onToggle={() => handleToggle(bot)}
            />
          ))}
        </motion.div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingBot ? 'Edit Keyword Bot' : 'Create Keyword Bot'}
        subtitle={editingBot ? 'Update your auto-reply bot settings' : 'Set up automatic responses for specific keywords'}
        size="xl"
      >
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Bot Name */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Bot Name *</label>
            <Input
              placeholder="e.g., Welcome Bot, FAQ Bot"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
            <Input
              placeholder="Optional description..."
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            />
          </div>

          {/* Match Type */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Match Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {MATCH_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFormData((prev) => ({ ...prev, matchType: option.value }))}
                  className={`p-2.5 rounded-lg border text-left transition-colors ${
                    formData.matchType === option.value
                      ? 'border-(--color-signal-orange) bg-(--color-signal-orange)/10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={formData.matchType === option.value ? 'text-(--color-signal-orange)' : 'text-gray-400'}>
                      {option.icon}
                    </span>
                    <span className="text-xs font-medium text-white">{option.label}</span>
                  </div>
                  <p className="text-[10px] text-gray-500">{option.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Keywords *</label>
            <div className="flex gap-2">
              <Input
                placeholder="Type a keyword and press Enter"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={handleKeywordKeyPress}
              />
              <Button onClick={handleAddKeyword} variant="secondary" size="sm">
                Add
              </Button>
            </div>
            {formData.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {formData.keywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-[#1A2332] rounded text-xs text-gray-300"
                  >
                    {keyword}
                    <button
                      onClick={() => handleRemoveKeyword(keyword)}
                      className="text-gray-500 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Reply Type */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Reply Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {REPLY_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFormData((prev) => ({ ...prev, replyType: option.value }))}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                    formData.replyType === option.value
                      ? 'border-(--color-signal-orange) bg-(--color-signal-orange)/10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <span className={formData.replyType === option.value ? 'text-(--color-signal-orange)' : 'text-gray-400'}>
                    {option.icon}
                  </span>
                  <span className="text-sm text-white">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Reply Content / Template Selection */}
          {formData.replyType === 'TEXT' ? (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-400">Reply Message *</label>
                <span className="text-[10px] text-gray-500">Use variables to personalize</span>
              </div>
              <textarea
                value={formData.replyContent}
                onChange={(e) => setFormData((prev) => ({ ...prev, replyContent: e.target.value }))}
                placeholder="Hi {{name}}! Thanks for reaching out..."
                rows={4}
                className="w-full px-3 py-2 bg-[#1A2332] border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-(--color-signal-orange)"
              />
              {/* Variable Insertion Buttons */}
              <div className="mt-2 p-2 bg-[#0D1117] rounded-lg border border-gray-800">
                <p className="text-[10px] text-gray-500 mb-2">Click to insert variable:</p>
                <div className="flex flex-wrap gap-1.5">
                  {REPLY_VARIABLES.map((v) => (
                    <button
                      key={v.variable}
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          replyContent: (prev.replyContent || '') + v.variable,
                        }));
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[#1A2332] hover:bg-[#252D3D] border border-gray-700 hover:border-(--color-signal-orange) rounded text-xs text-gray-300 hover:text-white transition-colors"
                      title={v.description}
                    >
                      <span className="text-(--color-signal-orange) font-mono">{v.variable}</span>
                      <span className="text-gray-500">({v.label})</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-500 mt-2 italic">
                  Variables will be replaced with actual contact data when the message is sent.
                </p>
              </div>

              {/* Quick Reply Buttons */}
              <div className="mt-4 p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs font-medium text-gray-400">Quick Reply Buttons</p>
                    <p className="text-[10px] text-gray-500">Add up to 3 buttons for quick responses</p>
                  </div>
                  <span className="text-xs text-gray-500">{formData.quickReplyButtons?.length || 0}/3</span>
                </div>
                
                {/* Existing buttons */}
                {formData.quickReplyButtons && formData.quickReplyButtons.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.quickReplyButtons.map((button, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          placeholder="Button title (max 20 chars)"
                          value={button.title}
                          onChange={(e) => {
                            const newButtons = [...(formData.quickReplyButtons || [])];
                            newButtons[idx] = { ...newButtons[idx], title: e.target.value.slice(0, 20) };
                            setFormData((prev) => ({ ...prev, quickReplyButtons: newButtons }));
                          }}
                          className="flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newButtons = formData.quickReplyButtons?.filter((_, i) => i !== idx);
                            setFormData((prev) => ({ ...prev, quickReplyButtons: newButtons }));
                          }}
                          className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add button */}
                {(!formData.quickReplyButtons || formData.quickReplyButtons.length < 3) && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const newButton: QuickReplyButton = {
                        id: `btn_${Date.now()}`,
                        title: '',
                      };
                      setFormData((prev) => ({
                        ...prev,
                        quickReplyButtons: [...(prev.quickReplyButtons || []), newButton],
                      }));
                    }}
                    className="w-full border-dashed"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Quick Reply Button
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Select Template *</label>
              <Select
                value={formData.templateId || ''}
                onChange={(value) => {
                  const v = Array.isArray(value) ? value[0] : value;
                  setFormData((prev) => ({ ...prev, templateId: v }));
                }}
                options={[
                  { value: '', label: 'Select a template...' },
                  ...templates.map((t) => ({ value: t.id, label: t.name })),
                ]}
                searchable
              />
              {templates.length === 0 && (
                <p className="text-[10px] text-yellow-500 mt-1">
                  No approved templates available. Create and approve templates first.
                </p>
              )}
            </div>
          )}

          {/* Advanced Settings Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white"
          >
            <Settings className="w-4 h-4" />
            Advanced Settings
            <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>

          {/* Advanced Settings */}
          <AnimatePresence>
            {showAdvanced && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-4 pt-2 border-t border-gray-800"
              >
                {/* Priority */}
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">
                    Priority (higher = checked first)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={1000}
                    value={formData.priority || 0}
                    onChange={(e) => setFormData((prev) => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                {/* Cooldown */}
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">
                    Cooldown (minutes)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={1440}
                    placeholder="No cooldown"
                    value={formData.cooldownMinutes || ''}
                    onChange={(e) => setFormData((prev) => ({ 
                      ...prev, 
                      cooldownMinutes: e.target.value ? parseInt(e.target.value) : undefined 
                    }))}
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Prevent spam by waiting before replying to same contact again
                  </p>
                </div>

                {/* Case Sensitive */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFormData((prev) => ({ ...prev, caseSensitive: !prev.caseSensitive }))}
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      formData.caseSensitive
                        ? 'bg-(--color-signal-orange) border-(--color-signal-orange)'
                        : 'border-gray-600'
                    }`}
                  >
                    {formData.caseSensitive && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <span className="text-sm text-gray-300">Case sensitive matching</span>
                </div>

                {/* Schedule */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFormData((prev) => ({ ...prev, enableSchedule: !prev.enableSchedule }))}
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      formData.enableSchedule
                        ? 'bg-(--color-signal-orange) border-(--color-signal-orange)'
                        : 'border-gray-600'
                    }`}
                  >
                    {formData.enableSchedule && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <span className="text-sm text-gray-300">Enable schedule</span>
                </div>

                {formData.enableSchedule && (
                  <div className="pl-7 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Start Time</label>
                        <Input
                          type="time"
                          value={formData.scheduleStart}
                          onChange={(e) => setFormData((prev) => ({ ...prev, scheduleStart: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">End Time</label>
                        <Input
                          type="time"
                          value={formData.scheduleEnd}
                          onChange={(e) => setFormData((prev) => ({ ...prev, scheduleEnd: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">Active Days</label>
                      <div className="flex gap-1">
                        {DAYS_OF_WEEK.map((day) => (
                          <button
                            key={day.value}
                            onClick={() => {
                              const days = formData.scheduleDays || [];
                              setFormData((prev) => ({
                                ...prev,
                                scheduleDays: days.includes(day.value)
                                  ? days.filter((d) => d !== day.value)
                                  : [...days, day.value],
                              }));
                            }}
                            className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                              (formData.scheduleDays || []).includes(day.value)
                                ? 'bg-(--color-signal-orange) text-white'
                                : 'bg-[#1A2332] text-gray-400 hover:bg-[#252D3D]'
                            }`}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-800">
            <Button variant="ghost" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !isFormValid()}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingBot ? (
                'Update Bot'
              ) : (
                'Create Bot'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, title: '', message: '', type: 'delete', bot: null })}
        onConfirm={handleConfirmAction}
        title={confirmDialog.title}
        description={confirmDialog.message}
        variant={
          confirmDialog.type === 'delete'
            ? 'danger'
            : confirmDialog.type === 'activate'
            ? 'success'
            : 'warning'
        }
        confirmText={
          confirmDialog.type === 'delete'
            ? 'Delete Bot'
            : confirmDialog.type === 'activate'
            ? 'Activate Bot'
            : 'Deactivate Bot'
        }
        cancelText="Cancel"
      />
    </motion.div>
  );
};

export default KeywordBotsPage;
